import { DesignSystemVersion, DsFile, ChatRef, DsVersionRef } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '@constants';
import {
	updateActiveDesignSystemVersion,
	updateDsChatRefs,
	updateDsVersionRefs,
	setDsPreparing,
	setDesignSystemLoadError,
} from '../redux/db/actions';
import { setDesignSystemContent, resolveOptimisticDsFileWrites } from '../redux/mode/actions';
import { createDesignSystemContentFromFiles } from '../designSystem/content/factory';
import { applyDsFileWrites, findResolvedDsFileWrites } from '../redux/mode/util/dsFiles';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

export class DesignSystemVersionPollingStream extends PollingStream<DesignSystemVersion | null> {
	private lastFiles: DsFile[] | null = null;
	private lastChatRefs: ChatRef[] = [];
	private lastVersionRefs: DsVersionRef[] = [];
	private lastPreparing: boolean = false;

	constructor(designSystemId: string, versionNumber?: number) {
		const base = VITE_FORGE_URL + `/poll/design-system-version/${designSystemId}`;
		const url = versionNumber != null ? `${base}?version=${versionNumber}` : base;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setDesignSystemLoadError('You do not have access to this design system'));
				store.dispatch(updateActiveDesignSystemVersion(null));
				store.dispatch(updateDsVersionRefs([]));
				store.dispatch(setDsPreparing(false));
				store.dispatch(setDesignSystemContent(null));
				store.dispatch(updateDsChatRefs([]));
			} else {
				console.error('DesignSystemVersionPollingStream error:', error);
			}
		};

		super(url, onError, 'activeDesignSystemVersion');
	}

	extractData(response: any): DesignSystemVersion[] {
		// DesignSystemVersion endpoint returns a single DesignSystemVersion object or null
		if (response?.version === null || response?.version === undefined) {
			this.lastFiles = null;
			this.lastChatRefs = [];
			this.lastVersionRefs = [];
			this.lastPreparing = false;
			return [];
		}

		this.lastFiles = response.files || null;
		this.lastChatRefs = response.chatRefs || [];
		this.lastVersionRefs = response.versionRefs || [];
		this.lastPreparing = response.preparing || false;

		return [response.version];
	}

	onData(versions: DesignSystemVersion[]) {
		const version = versions.length > 0 ? versions[0] : null;
		if (!version) {
			store.dispatch(updateActiveDesignSystemVersion(null));
			store.dispatch(updateDsVersionRefs([]));
			store.dispatch(setDsPreparing(false));
			store.dispatch(setDesignSystemContent(null));
			store.dispatch(updateDsChatRefs([]));
			return;
		}

		// The backend already returns DesignSystemVersion objects with proper camelCase field names
		// (thanks to postgres.camel transform)
		// We just need to convert date strings to Date objects
		const processedVersion: DesignSystemVersion = {
			...version,
			createdAt: new Date(version.createdAt),
			updatedAt: new Date(version.updatedAt),
		};

		// since DS versions don't have optimistic writes (they aren't changed on the FE currently) we don't need to resolve them
		store.dispatch(updateActiveDesignSystemVersion(processedVersion));

		// Process chat refs - convert date strings to Date objects
		const processedChatRefs: ChatRef[] = this.lastChatRefs.map((ref) => ({
			...ref,
			updatedAt: new Date(ref.updatedAt),
		}));
		store.dispatch(updateDsChatRefs(processedChatRefs));

		// Update version refs - convert date strings to Date objects
		const processedVersionRefs: DsVersionRef[] = this.lastVersionRefs.map((ref) => ({
			...ref,
			createdAt: new Date(ref.createdAt),
		}));
		store.dispatch(updateDsVersionRefs(processedVersionRefs));
		store.dispatch(setDsPreparing(this.lastPreparing));

		// Process files, applying optimistic writes and resolving outcome-matched writes.
		const serverFiles = this.lastFiles || [];
		const state = store.getState();
		const writes = state.mode.optimisticDsFileWrites;

		if (serverFiles.length > 0 || writes.length > 0) {
			const resolvedIds = findResolvedDsFileWrites(writes, serverFiles);
			if (resolvedIds.length > 0) {
				store.dispatch(resolveOptimisticDsFileWrites(resolvedIds));
			}

			const remainingWrites = writes.filter((w) => !resolvedIds.includes(w.id));
			const patchedFiles = applyDsFileWrites(serverFiles, remainingWrites);

			const content = createDesignSystemContentFromFiles(processedVersion.id, patchedFiles);
			store.dispatch(setDesignSystemContent(content));
		} else {
			// If no files, set content to null and clear optimistic writes
			store.dispatch(setDesignSystemContent(null));
		}
	}
}
